import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Check, HelpCircle, Info } from "lucide-react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { AdjustDirection, AdjustOutcome } from "../../types/cardWallets.types";
import { ADJUST_CURRENCY, shortWalletId, signedMovementValue } from "../../utils/cardWalletAdjust";

/** What a `[Comprobar]` run concluded, when it has already run. */
export type UnconfirmedNote = "not_placed" | "unknown" | null;

export interface AdjustBalanceResultProps {
  outcome: AdjustOutcome;
  direction: AdjustDirection;
  organizationName: string;
  walletId: string;
  magnitude: string;
  unconfirmedNote: UnconfirmedNote;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-4 border-b border-border px-5 py-3.5 last:border-b-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium">{value}</dd>
    </div>
  );
}

/**
 * Step 3 — the receipt, or the one thing that happened instead.
 *
 * Every failure is its own designed state with its own next action: the duplicate reference is a warning and
 * not an error (the money moved once, on the first try), and the lost answer offers a check rather than a
 * retry, because repeating the same reference is exactly what must not happen.
 */
export function AdjustBalanceResult({
  outcome,
  direction,
  organizationName,
  walletId,
  magnitude,
  unconfirmedNote,
}: AdjustBalanceResultProps) {
  const { t } = useTranslations("cardWallets");
  const isCredit = direction === "credit";

  if (outcome.kind === "done") {
    const DirectionIcon = isCredit ? ArrowDownLeft : ArrowUpRight;
    return (
      <article
        aria-labelledby="card-wallet-receipt-label"
        className="overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div className="flex flex-col gap-2.5 border-b border-border bg-primary/5 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <span
              id="card-wallet-receipt-label"
              className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
            >
              {t("done_label")}
            </span>
            <ColocaStatusPill variant={isCredit ? "success" : "danger"} icon={Check}>
              {t(isCredit ? "done_pill_credit" : "done_pill_debit")}
            </ColocaStatusPill>
          </div>
          <span className="break-all font-mono text-sm font-semibold">{outcome.reference}</span>
        </div>

        <div className="flex flex-col gap-1 border-b border-border px-5 pb-5 pt-5">
          <span className="text-sm text-muted-foreground">{t("done_amount")}</span>
          <span className={cn("flex items-center gap-2", isCredit ? "text-success" : "text-destructive")}>
            <DirectionIcon className="size-5 shrink-0" aria-hidden="true" />
            <ColocaAmountDisplay
              size="xl"
              signed
              value={signedMovementValue(magnitude, direction)}
              currency={ADJUST_CURRENCY}
            />
          </span>
        </div>

        <dl>
          <DetailRow label={t("review_organization")} value={organizationName} />
          <DetailRow
            label={t("review_wallet")}
            value={t("review_wallet_value", { short: shortWalletId(walletId) })}
          />
          <DetailRow
            label={t("done_balance_before")}
            value={
              <ColocaAmountDisplay size="sm" value={outcome.previousBalance} currency={ADJUST_CURRENCY} />
            }
          />
        </dl>
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-4 bg-muted/40 px-5 py-4">
          <span className="text-sm font-semibold">{t("done_balance_after")}</span>
          <ColocaAmountDisplay size="lg" value={outcome.newBalance} currency={ADJUST_CURRENCY} />
        </div>
      </article>
    );
  }

  if (outcome.kind === "duplicate_reference") {
    return (
      <div className="space-y-3">
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          heading={t("error_duplicate_title")}
          text={t("error_duplicate_body", { reference: outcome.reference })}
        />
      </div>
    );
  }

  if (outcome.kind === "unconfirmed") {
    return (
      <div className="space-y-3">
        <ColocaAlert
          variant="warning"
          icon={HelpCircle}
          heading={t("error_unconfirmed_title")}
          text={t("error_unconfirmed_body")}
        />
        <div className="flex flex-col gap-1 rounded-lg border border-border bg-muted/30 px-3.5 py-3">
          <span className="text-xs text-muted-foreground">
            {t("review_reference")} ·{" "}
            <ColocaAmountDisplay
              size="xs"
              signed
              value={signedMovementValue(magnitude, direction)}
              currency={ADJUST_CURRENCY}
            />
          </span>
          <span className="break-all font-mono text-sm">{outcome.reference}</span>
        </div>
        {unconfirmedNote === "not_placed" && (
          <ColocaAlert variant="secondary" icon={Info} text={t("error_unconfirmed_not_placed")} />
        )}
        {unconfirmedNote === "unknown" && (
          <ColocaAlert variant="warning" icon={AlertTriangle} text={t("error_unconfirmed_still")} />
        )}
      </div>
    );
  }

  const messageByKind = {
    ineligible_wallet: { variant: "warning" as const, text: t("error_ineligible") },
    currency_mismatch: { variant: "destructive" as const, text: t("error_currency") },
    wallet_not_found: { variant: "destructive" as const, text: t("error_not_found") },
    no_permission: { variant: "destructive" as const, text: t("error_permission") },
    generic: { variant: "destructive" as const, text: outcome.kind === "generic" ? outcome.message : "" },
  };
  const view = messageByKind[outcome.kind];

  return <ColocaAlert variant={view.variant} icon={AlertTriangle} text={view.text || t("error_generic")} />;
}
